import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Shield, Plus, Trash2, X } from 'lucide-react'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { useOrganization } from '../contexts/OrganizationContext'

interface SecurityGroupMapping {
  id: string
  organization_id: string
  entra_group_id: string
  group_display_name: string | null
  permission_key: string
  is_active: boolean
  created_at: string
}

interface PermissionKeyInfo {
  key: string
  label_fi: string
  label_en: string
}

export default function SecurityGroupMappingsPage() {
  const { t, i18n } = useTranslation('admin')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const { selectedOrgId } = useOrganization()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newGroupId, setNewGroupId] = useState('')
  const [newGroupName, setNewGroupName] = useState('')
  const [newPermissionKey, setNewPermissionKey] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const orgParam = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''

  const { data: mappingsData, isLoading } = useQuery({
    queryKey: ['security-group-mappings', selectedOrgId],
    queryFn: () =>
      apiClient.get<{ data: SecurityGroupMapping[] }>(`/security-groups/mappings${orgParam}`),
  })

  const { data: permKeysData } = useQuery({
    queryKey: ['security-group-permissions'],
    queryFn: () =>
      apiClient.get<{ keys: PermissionKeyInfo[] }>('/security-groups/permissions'),
  })

  const createMutation = useMutation({
    mutationFn: (data: {
      entra_group_id: string
      group_display_name: string | null
      permission_key: string
      organization_id?: string
    }) => apiClient.post('/security-groups/mappings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security-group-mappings'] })
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/security-groups/mappings/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security-group-mappings'] })
      setDeleteConfirmId(null)
    },
  })

  const resetForm = () => {
    setNewGroupId('')
    setNewGroupName('')
    setNewPermissionKey('')
    setIsModalOpen(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newGroupId.trim() || !newPermissionKey) return
    createMutation.mutate({
      entra_group_id: newGroupId.trim(),
      group_display_name: newGroupName.trim() || null,
      permission_key: newPermissionKey,
      ...(selectedOrgId ? { organization_id: selectedOrgId } : {}),
    })
  }

  const getPermissionLabel = (key: string): string => {
    const info = permKeysData?.keys?.find((k) => k.key === key)
    if (!info) return key
    return i18n.language === 'fi' ? info.label_fi : info.label_en
  }

  const mappings = mappingsData?.data ?? []

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="w-7 h-7 text-primary-600" />
            {t('securityGroups.title') || 'Ryhmäoikeudet'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('securityGroups.subtitle') || 'Yhdistä Azure AD -tietoturvaryhmiä käyttöoikeuksiin'}
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
        >
          <Plus className="w-4 h-4" />
          {t('securityGroups.addMapping') || 'Lisää määritys'}
        </button>
      </div>

      {/* Mappings Table */}
      {mappings.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{t('securityGroups.noMappings') || 'Ei ryhmämäärityksiä'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('securityGroups.groupName') || 'Ryhmän nimi'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('securityGroups.groupId') || 'Group Object ID'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('securityGroups.permission') || 'Oikeus'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {tc('common.actions') || 'Toiminnot'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mappings.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {m.group_display_name || (
                      <span className="text-gray-400 italic">
                        {t('securityGroups.noName') || 'Ei nimeä'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                    {m.entra_group_id}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getPermissionLabel(m.permission_key)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {deleteConfirmId === m.id ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-xs text-red-600">
                          {t('securityGroups.confirmDelete') || 'Poistetaanko?'}
                        </span>
                        <button
                          onClick={() => deleteMutation.mutate(m.id)}
                          disabled={deleteMutation.isPending}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          {tc('common.yes') || 'Kyllä'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="text-gray-500 hover:text-gray-700 text-sm"
                        >
                          {tc('common.cancel')}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirmId(m.id)}
                        className="text-red-500 hover:text-red-700"
                        title={tc('common.delete') || 'Poista'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Mapping Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">
                {t('securityGroups.addMapping') || 'Lisää ryhmämääritys'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('securityGroups.groupId') || 'Group Object ID'} *
                </label>
                <input
                  type="text"
                  value={newGroupId}
                  onChange={(e) => setNewGroupId(e.target.value)}
                  placeholder="aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee"
                  required
                  maxLength={36}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {t('securityGroups.groupIdHelp') || 'Azure AD -ryhmän Object ID (UUID)'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('securityGroups.groupName') || 'Ryhmän nimi'}
                </label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t('securityGroups.groupNamePlaceholder') || 'esim. Ambulanssitilaukset'}
                  maxLength={255}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('securityGroups.permission') || 'Oikeus'} *
                </label>
                <select
                  value={newPermissionKey}
                  onChange={(e) => setNewPermissionKey(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">{t('securityGroups.selectPermission') || 'Valitse oikeus...'}</option>
                  {permKeysData?.keys?.map((pk) => (
                    <option key={pk.key} value={pk.key}>
                      {i18n.language === 'fi' ? pk.label_fi : pk.label_en}
                    </option>
                  ))}
                </select>
              </div>

              {createMutation.isError && (
                <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                  {(createMutation.error as { response?: { data?: { detail?: { code?: string } } } })?.response?.data?.detail?.code === 'MAPPING_ALREADY_EXISTS'
                    ? (t('securityGroups.mappingExists') || 'Tämä ryhmä-oikeus-yhdistelmä on jo olemassa')
                    : (t('securityGroups.createError') || 'Ryhmämäärityksen luonti epäonnistui')}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  {tc('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || !newGroupId.trim() || !newPermissionKey}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {createMutation.isPending
                    ? (tc('common.saving') || 'Tallennetaan...')
                    : (tc('common.save') || 'Tallenna')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
